<style lang="scss">
.booking-slots-page {
  span[cy="calendar-view"] {
    @include mobile() {
      display: none !important;
    }
  }
}
</style>
<template>
  <div class="theme-color-wrapper p-2 booking-slots-page">
    <div class="calls row">
      <section class="col-12 mb-2">
        <h2 cy="page-title" class="font-weight-bolder mb-1">Call Booking Slots</h2>
        <router-link :to="{ name: 'dashboard' }" class="body-text">
          &laquo; Back to dashboard
        </router-link>
      </section>
      <div class="col-12">
        <section v-if="!loading">
          <div class="row align-items-center my-2">
            <div class="col-sm-4" v-if="!calendarView">
              <div class="form-group">
                <label>Day of week</label>
                <select class="form-control form-control-sm" cy="slots-filter-day" v-model="filters.day" @change="saveFilters">
                  <option value="All">All</option>
                  <option v-for="(day, index) in weekdays" :value="day" :key="index">{{ day }}</option>
                </select>
              </div>
            </div>
            <div class="col-sm-4 align-items-center" style="margin-top: 8px">
                  <span v-if="calendarView" @click="showCalendarView(false)" class="d-block pointer" cy="list-view">
                    <i class="fas fa-list"></i> List View
                  </span>
              <span v-if="!calendarView" @click="showCalendarView(true)" class="d-block pointer" cy="calendar-view">
                   <i class="fas fa-list"></i> Calendar View
                 </span>
            </div>
            <div class="col-sm-4 mt-2 mt-sm-0 ml-auto d-flex align-items-center">
              <router-link :to="{name: 'create-booking-slot', params: {prisonId: prison.prison_id}}"
                           class="btn btn-primary mr-1">Add new slot
              </router-link>

              <button class="btn btn-danger" @click="removeAllSlots">Remove all slots</button>
            </div>
          </div>

          <b-container v-if="!calendarView" class="px-0">
            <b-table
              class="data-table" cy="slot-table"
              hover
              :items="filteredBookingSlots"
              :fields="columns"
              :current-page="currentPage"
              :per-page="perPage"
              :responsive="true"
            >
              <template #cell(time)="data">
                {{ data.item.slot_begin }} - {{ data.item.slot_end }}
              </template>

              <template #cell(start_date)="data">
                {{data.item.start_date | formatDate}}
              </template>

              <template #cell(end_date)="data">
                {{data.item.end_date | formatDate}}
              </template>

              <template #cell(actions)="data">
                <b-dropdown class="m-0" variant="link" right toggle-class="text-decoration-none" no-caret>
                  <template #button-content>
                    <i class="fas fa-ellipsis-v"></i>
                  </template>
                  <b-dropdown-item-button class="full-nested-btn"
                                          @click="$router.push({ name: 'update-booking-slot', params: { prisonId: data.item.prison_id, bookingSlotId: data.item._id }})">
                    Edit
                  </b-dropdown-item-button>
                  <b-dropdown-item-button class="full-nested-btn"
                                          @click="deleteSlot(data.item)">
                    Remove
                  </b-dropdown-item-button>
                </b-dropdown>
              </template>
            </b-table>
            <b-row>
              <b-col md="6" class="my-1">
                <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="my-0"/>
              </b-col>
            </b-row>
          </b-container>
          <p v-if="bookingSlots.length === 0 && !calendarView" class="body-text text-center mt-5">
            No Booking Slots
          </p>
          <div v-if="calendarView">
            <booking-slots-calendar
              @slot-deactivate="deleteSlot($event)"
              @slot-edit="editSlot($event)"
              :slots="filteredBookingSlots">
            </booking-slots-calendar>
          </div>


        </section>
        <div v-else-if="loading" class="mt-1">
          <b-spinner class="d-block" variant="primary"></b-spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import BookingSlotService from '@/services/BookingSlotService';
import {logger} from '@/util/logger';
import {ERROR_CODES} from '@/util/errorCodes';
import BookingSlotsCalendar from './BookingSlotsCalendar';

const LOCAL_STORAGE_FILTERS = {
  DAY: 'bookingSlotsDay',
  STATUS: 'bookingSlotsStatus',
};

export default {
  name: 'booking-slots-table',
  components: {
    BookingSlotsCalendar
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      prison: state => state.prison.prison,
    }),
    filteredBookingSlots() {
      let filteredBookingSlots = this.bookingSlots || [];
      if (this.filters.day && this.filters.day !== 'All') {
        // eslint-disable-next-line max-len
        filteredBookingSlots = filteredBookingSlots.filter(bs => bs.day_of_week.toLowerCase() === this.filters.day.toLowerCase());
      }
      if (this.filters.status && this.filters.status !== 'all') {
        filteredBookingSlots = filteredBookingSlots.filter((bs) => {
          if (this.filters.status === 'active') {
            return bs.active === true;
          }
          return bs.active === false;
        });
      }
      return filteredBookingSlots;
    },
    totalRows() {
      return this.filteredBookingSlots.length;
    },
  },
  data() {
    let day = 'All';
    if (localStorage.getItem(LOCAL_STORAGE_FILTERS.DAY)) {
      day = localStorage.getItem(LOCAL_STORAGE_FILTERS.DAY);
    }
    let status = 'active';
    if (localStorage.getItem(LOCAL_STORAGE_FILTERS.STATUS)) {
      status = localStorage.getItem(LOCAL_STORAGE_FILTERS.STATUS);
    }
    return {
      loading: true,
      calendarView: false,
      bookingSlots: [],
      weekdays: BookingSlotService.getWeekdays(),
      columns: [
        {
          key: 'day_of_week',
          label: 'Day',
        },
        {
          key: 'time',
          label: 'Slot Time',
        },
        {
          key: 'start_date',
          label: 'Start Date',
        },
        {
          key: 'end_date',
          label: 'End Date',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
      currentPage: 1,
      perPage: 10,
      filters: {
        day,
        status: 'active'
      },
    };
  },
  async mounted() {
    await this.$store.dispatch('prison/getPrison');
    this.setCalendarViewFromLocal();
    this.getBookingSlots();
  },
  methods: {
    setCalendarViewFromLocal() {
      let view = localStorage.getItem('calendarView');

      if (view && view === 'true') {
        this.showCalendarView(true);
      }
    },
    showCalendarView(value) {
      this.calendarView = value;
      localStorage.setItem('calendarView', value);
      if (value) {
        this.filters.day = 'All';
      }
    },
    async getBookingSlots() {
      this.loading = true;

      const slots = await this.$store.dispatch('bookingSlots/getBookingSlots');
      let mondaySlots = [];
      let tuesdaySlots = [];
      let wednesdaySlots = [];
      let thursdaySlots = [];
      let fridaySlots = [];
      let saturdaySlots = [];
      let sundaySlots = [];
      // Add slots to their days
      slots.forEach((bs) => {
        switch (bs.day_of_week.toLowerCase()) {
          case 'monday':
            mondaySlots.push(bs);
            break;
          case 'tuesday':
            tuesdaySlots.push(bs);
            break;
          case 'wednesday':
            wednesdaySlots.push(bs);
            break;
          case 'thursday':
            thursdaySlots.push(bs);
            break;
          case 'friday':
            fridaySlots.push(bs);
            break;
          case 'saturday':
            saturdaySlots.push(bs);
            break;
          case 'sunday':
            sundaySlots.push(bs);
            break;
          default:
            console.error('Cant read day of week for ', bs);
            break;
        }
      });
      // Sort each booking slot based on date
      mondaySlots = mondaySlots.sort(this.sortStartTime);
      tuesdaySlots = tuesdaySlots.sort(this.sortStartTime);
      wednesdaySlots = wednesdaySlots.sort(this.sortStartTime);
      thursdaySlots = thursdaySlots.sort(this.sortStartTime);
      fridaySlots = fridaySlots.sort(this.sortStartTime);
      saturdaySlots = saturdaySlots.sort(this.sortStartTime);
      sundaySlots = sundaySlots.sort(this.sortStartTime);
      this.bookingSlots = [
        ...mondaySlots,
        ...tuesdaySlots,
        ...wednesdaySlots,
        ...thursdaySlots,
        ...fridaySlots,
        ...saturdaySlots,
        ...sundaySlots,
      ];
      this.loading = false;
    },
    sortStartTime(bs1, bs2) {
      const bs1Date = new Date();
      bs1Date.setHours(bs1.slot_begin.split(':')[0], bs1.slot_begin.split(':')[1], 0, 0);
      const bs2Date = new Date();
      bs2Date.setHours(bs2.slot_begin.split(':')[0], bs2.slot_begin.split(':')[1], 0, 0);
      return bs1Date - bs2Date;
    },
    saveFilters() {
      if (this.filters.day && this.filters.day.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_FILTERS.DAY, this.filters.day);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_FILTERS.DAY);
      }
      if (this.filters.status && this.filters.status.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_FILTERS.STATUS, this.filters.status);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_FILTERS.STATUS);
      }
    },
    deleteSlot(item) {
      this.$swal({
        title: 'Are you sure?',
        text: `Are you sure you want to delete slot ${item.name}?`,
        icon: 'info',
        buttons: true,
      }).then((value) => {
        if (value) {
          this.setBookingSetInactive(item);
        }
      });
    },
    setBookingSetInactive(slot) {
      let slotClone = Object.assign({}, slot);
      slotClone.active = false;

      BookingSlotService.updateBookingSlot(slotClone._id, slotClone).then(() => {
        this.$toast.success('Booking slot successfully deleted', {
          toastClassName: ['toast-std', 'success-toast'],
        });
        this.getBookingSlots(); // refresh the slots
      }).catch(err => {
        console.log('Error deleting booking slot ', err.response);
        // eslint-disable-next-line max-len
        if (err && err.response && err.response.data && err.response.data.error === ERROR_CODES.BOOKING_SLOTS.OVERLAP) {
          this.displayBookingSlotOverlapModal();
        } else {
          this.$toast.error('Error deleting the booking slot', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        }
      });
    },
    removeAllSlots() {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete all slots?',
        icon: 'info',
        buttons: true,
      }).then((value) => {
        if (value) {
          BookingSlotService.setAllBookingSlotsInactive(this.prison.prison_id).then((res) => {
            this.$toast.success('Successfully deleted all booking slots', {
              toastClassName: ['toast-std', 'success-toast'],
            });
            this.getBookingSlots(); // refresh booking slots
          }).catch((err) => {
            console.log('error deleting booking slots', err);
            this.$toast.error('Error deleting all booking slots', {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          });
        }
      });
    },
    editSlot(slot) {
      this.$router.push({name: 'update-booking-slot', params: {bookingSlotId: slot._id}})
    }
  },
};
</script>
